import React from 'react';
import { motion } from 'framer-motion';
import { HeartPulseIcon, BrainIcon, ShieldCheckIcon, AwardIcon, ArrowRightIcon, UsersIcon, StethoscopeIcon, GraduationCapIcon } from 'lucide-react';
export function Home() {
  return <main className="pb-24 lg:pb-0" data-id="element-185">
      {/* Hero Section - Z-Pattern */}
      <section className="relative min-h-[80vh] flex items-center overflow-hidden bg-[#F8F9FA]" data-id="element-186">
        <div className="container mx-auto px-4 grid lg:grid-cols-2 gap-12 items-center py-12 lg:py-24" data-id="element-187">
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.8
        }} className="z-10" data-id="element-188">
            <span className="inline-block px-4 py-1 rounded-full bg-[#588157]/10 text-[#588157] font-semibold text-sm mb-6 uppercase tracking-wider" data-id="element-189">
              Restoring Health • Renewing Minds • Rebuilding Lives
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-serif text-[#588157] leading-tight mb-6" data-id="element-190">
              Don't just manage your health.{' '}
              <span className="italic text-[#D4A373]" data-id="element-191">Reclaim your life.</span>
            </h1>
            <p className="text-xl text-[#1A374D]/80 mb-8 max-w-xl leading-relaxed" data-id="element-192">
              Global Restorative Health & Wellness LLC is dedicated to
              delivering holistic, compassionate, and evidence-based services
              that address the physical, emotional, and psychological needs of
              individuals and families.
            </p>
            <div className="flex flex-col sm:flex-row gap-4" data-id="element-193">
              <button onClick={() => window.location.hash = '#contact-us'} className="px-8 py-4 bg-[#588157] text-white font-bold rounded-full hover:bg-[#4a6d49] transition-all shadow-lg flex items-center justify-center gap-2" data-id="element-194">
                Start Your Restoration <ArrowRightIcon size={20} data-id="element-195" />
              </button>
              <button onClick={() => window.location.hash = '#services'} className="px-8 py-4 border-2 border-[#588157] text-[#588157] font-bold rounded-full hover:bg-[#588157]/5 transition-all flex items-center justify-center gap-2" data-id="element-196">
                Our Services
              </button>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 1
        }} className="relative" data-id="element-197">
            <div className="aspect-[4/5] rounded-2xl overflow-hidden shadow-2xl" data-id="element-198">
              <img src="https://images.unsplash.com/photo-1511895426328-dc8714191300?auto=format&fit=crop&q=80&w=1000" alt="Healthy, diverse family walking in nature" className="w-full h-full object-cover" data-id="element-199" />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-xl hidden md:block max-w-xs" data-id="element-200">
              <div className="flex items-center gap-3 mb-2" data-id="element-201">
                <ShieldCheckIcon className="text-[#588157]" data-id="element-202" />
                <span className="font-bold text-[#588157]" data-id="element-203">
                  HIPAA Compliant
                </span>
              </div>
              <p className="text-sm text-[#1A374D]/70" data-id="element-204">
                Your health data and privacy are our highest priority.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Split-Entry UI */}
      <section className="py-16 lg:py-20 bg-white" data-id="element-205">
        <div className="container mx-auto px-4" data-id="element-206">
          <div className="text-center mb-12 lg:mb-16" data-id="element-207">
            <h2 className="text-3xl sm:text-4xl font-serif text-[#588157] mb-4" data-id="element-208">
              Integrated Healthcare Pathways
            </h2>
            <p className="text-[#1A374D]/70 max-w-2xl mx-auto" data-id="element-209">
              Our approach focuses on prevention, restoration, and
              empowerment—helping clients regain control of their health and
              rebuild resilience.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8" data-id="element-210">
            <motion.div whileHover={{
            y: -5
          }} className="group p-8 rounded-3xl bg-[#588157] text-white relative overflow-hidden" data-id="element-211">
              <div className="relative z-10" data-id="element-212">
                <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center mb-6" data-id="element-213">
                  <StethoscopeIcon size={32} data-id="element-214" />
                </div>
                <h3 className="text-3xl font-serif mb-4" data-id="element-215">
                  Medical & Wellness Services
                </h3>
                <p className="text-white/80 mb-8 text-lg leading-relaxed" data-id="element-216">
                  Comprehensive family practice and lifestyle medicine led by
                  experienced clinical leadership.
                </p>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8" data-id="element-217">
                  {['Family Practice Care', 'Chronic Disease Management', 'Health Risk Assessments', 'Lifestyle Counseling', 'Preventive Wellness Exams', 'Critical Care Consultation', 'Post-hospital Recovery', 'Organizational Wellness'].map(item => <li key={item} className="flex items-center gap-2 text-sm" data-id="element-218">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#D4A373] flex-shrink-0" data-id="element-219" />
                      <span data-id="element-220">{item}</span>
                    </li>)}
                </ul>
                <button onClick={() => window.location.hash = '#services'} className="w-full py-4 bg-white text-[#588157] font-bold rounded-xl hover:bg-[#F8F9FA] transition-colors" data-id="element-221">
                  Explore Medical Care
                </button>
              </div>
              <div className="absolute -right-20 -bottom-20 w-64 h-64 bg-white/5 rounded-full blur-3xl" data-id="element-222" />
            </motion.div>

            <motion.div whileHover={{
            y: -5
          }} className="group p-8 rounded-3xl bg-[#F8F9FA] border-2 border-[#588157]/10 relative overflow-hidden" data-id="element-223">
              <div className="relative z-10" data-id="element-224">
                <div className="w-16 h-16 bg-[#588157]/10 rounded-2xl flex items-center justify-center mb-6 text-[#588157]" data-id="element-225">
                  <BrainIcon size={32} data-id="element-226" />
                </div>
                <h3 className="text-3xl font-serif text-[#588157] mb-4" data-id="element-227">
                  SYMBIS Relationship & Marriage Coaching
                </h3>
                <p className="text-[#1A374D]/80 mb-8 text-lg leading-relaxed" data-id="element-228">
                  Board-certified coaching and trauma-informed support for
                  emotional and psychological well-being, specializing in
                  relationship restoration.
                </p>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8 text-[#1A374D]/80" data-id="element-229">
                  {['Marriage & Family Coaching', 'SYMBIS Facilitation', 'Trauma & PTSD Support', 'Grief & Loss Support', 'Anxiety & Depression', 'Youth & Adolescent', 'Geriatric Mental Wellness', 'Life Recovery & Purpose'].map(item => <li key={item} className="flex items-center gap-2 text-sm" data-id="element-230">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#D4A373] flex-shrink-0" data-id="element-231" />
                      <span data-id="element-232">{item}</span>
                    </li>)}
                </ul>
                <button onClick={() => window.location.hash = '#symbis-coaching'} className="w-full py-4 bg-[#588157] text-white font-bold rounded-xl hover:bg-[#4a6d49] transition-colors" data-id="element-233">
                  Explore SYMBIS Coaching
                </button>
              </div>
              <div className="absolute -right-20 -bottom-20 w-64 h-64 bg-[#588157]/5 rounded-full blur-3xl" data-id="element-234" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission & Vision Section */}
      <section className="py-20 bg-[#F8F9FA]" data-id="element-235">
        <div className="container mx-auto px-4" data-id="element-236">
          <div className="grid md:grid-cols-2 gap-12" data-id="element-237">
            <div className="p-10 bg-white rounded-3xl shadow-sm border border-[#588157]/5" data-id="element-238">
              <h3 className="text-2xl font-serif text-[#588157] mb-6 flex items-center gap-3" data-id="element-239">
                <div className="w-10 h-10 bg-[#588157] rounded-full flex items-center justify-center text-white" data-id="element-240">
                  <HeartPulseIcon size={20} data-id="element-241" />
                </div>
                Our Mission
              </h3>
              <p className="text-lg text-[#1A374D]/80 leading-relaxed italic" data-id="element-242">
                "To restore health, promote emotional wellness, and empower
                individuals and families to live healthier, more meaningful, and
                purpose-driven lives."
              </p>
            </div>
            <div className="p-10 bg-white rounded-3xl shadow-sm border border-[#588157]/5" data-id="element-243">
              <h3 className="text-2xl font-serif text-[#588157] mb-6 flex items-center gap-3" data-id="element-244">
                <div className="w-10 h-10 bg-[#D4A373] rounded-full flex items-center justify-center text-white" data-id="element-245">
                  <ShieldCheckIcon size={20} data-id="element-246" />
                </div>
                Our Vision
              </h3>
              <p className="text-lg text-[#1A374D]/80 leading-relaxed" data-id="element-247">
                To become a trusted leader in integrated healthcare and mental
                wellness services locally, nationally, and globally.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values Section */}
      <section className="py-20 bg-white" data-id="element-248">
        <div className="container mx-auto px-4" data-id="element-249">
          <h2 className="text-3xl font-serif text-[#588157] text-center mb-12" data-id="element-250">
            Our Core Values
          </h2>
          <div className="flex flex-wrap justify-center gap-4" data-id="element-251">
            {['Compassion', 'Integrity', 'Excellence', 'Confidentiality', 'Professionalism', 'Respect', 'Empowerment'].map(value => <div key={value} className="px-6 py-3 bg-[#E2E8E4] text-[#588157] font-bold rounded-full text-sm uppercase tracking-widest" data-id="element-252">
                {value}
              </div>)}
          </div>
        </div>
      </section>

      {/* Leadership Feature */}
      <section className="py-16 lg:py-24 bg-[#E2E8E4]" data-id="element-253">
        <div className="container mx-auto px-4" data-id="element-254">
          <div className="flex flex-col lg:flex-row gap-12 lg:gap-16 items-center" data-id="element-255">
            <div className="w-full lg:w-1/2" data-id="element-256">
              <h2 className="text-3xl sm:text-4xl font-serif text-[#588157] mb-6" data-id="element-257">
                Expertise Rooted in Compassion
              </h2>
              <p className="text-lg text-[#1A374D]/80 mb-8 leading-relaxed" data-id="element-258">
                Our leadership team brings over 20 years of combined clinical
                medical experience and advanced expertise in mental health
                coaching and trauma recovery.
              </p>

              <div className="space-y-8" data-id="element-259">
                <div className="flex flex-col sm:flex-row gap-6 p-6 bg-white rounded-2xl shadow-sm" data-id="element-260">
                  <div className="w-24 h-24 rounded-full bg-[#588157]/10 flex-shrink-0 overflow-hidden mx-auto sm:mx-0 flex items-center justify-center text-[#588157]" data-id="element-261">
                    <StethoscopeIcon size={48} data-id="element-262" />
                  </div>
                  <div data-id="element-263">
                    <h4 className="text-xl font-serif text-[#588157] font-bold" data-id="element-264">
                      The Founder, NP
                    </h4>
                    <p className="text-[#D4A373] font-semibold text-sm mb-2 uppercase tracking-wider" data-id="element-265">
                      Board Certified Nurse Practitioner
                    </p>
                    <p className="text-sm text-[#1A374D]/70 mb-4" data-id="element-266">
                      Expertise in family practice, critical care support,
                      chronic disease management, and preventive health across
                      private and government hospital systems.
                    </p>
                    <div className="flex flex-wrap gap-2" data-id="element-267">
                      {['Family Practice', 'Critical Care', 'HRAs', 'Care Coordination'].map(tag => <span key={tag} className="text-[10px] px-2 py-1 bg-[#588157]/5 text-[#588157] rounded uppercase font-bold" data-id="element-268">
                          {tag}
                        </span>)}
                    </div>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-6 p-6 bg-white rounded-2xl shadow-sm" data-id="element-269">
                  <div className="w-24 h-24 rounded-full bg-[#588157]/10 flex-shrink-0 overflow-hidden mx-auto sm:mx-0" data-id="element-270">
                    <img src="https://cdn.magicpatterns.com/uploads/kb2JwH8nXzaWT4JHRhcA6K/WhatsApp-Image-2026-04-24-at-3.36.19-PM-1.jpg" alt="Joel Osebor - Co-Founder" className="w-full h-full object-cover" data-id="element-271" />
                  </div>
                  <div data-id="element-272">
                    <h4 className="text-xl font-serif text-[#588157] font-bold" data-id="element-273">
                      Joel Osebor
                    </h4>
                    <p className="text-[#D4A373] font-semibold text-sm mb-2 uppercase tracking-wider" data-id="element-274">
                      Co-Founder & Mental Wellness Director
                    </p>
                    <p className="text-sm text-[#1A374D]/70 mb-4" data-id="element-275">
                      8 years as a SYMBIS Marriage Facilitator and Clinical
                      Intern at Safe Harbor Behavioral Care. Specializing in
                      relationship restoration, trauma recovery, and life
                      recovery coaching.
                    </p>
                    <div className="flex flex-wrap gap-2" data-id="element-276">
                      {['Marriage Repair', 'Trauma Recovery', 'Grief & Loss', 'SYMBIS'].map(tag => <span key={tag} className="text-[10px] px-2 py-1 bg-[#588157]/5 text-[#588157] rounded uppercase font-bold" data-id="element-277">
                          {tag}
                        </span>)}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="w-full lg:w-1/2 grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-278">
              <div className="space-y-4 sm:pt-12" data-id="element-279">
                <div className="aspect-square bg-[#588157] rounded-2xl flex flex-col items-center justify-center text-white p-6 text-center" data-id="element-280">
                  <AwardIcon size={40} className="mb-4 text-[#D4A373]" data-id="element-281" />
                  <span className="text-2xl font-serif font-bold" data-id="element-282">
                    Board Certified
                  </span>
                  <span className="text-xs uppercase tracking-widest opacity-70" data-id="element-283">
                    Clinical Excellence
                  </span>
                </div>
                <div className="aspect-square bg-white rounded-2xl flex flex-col items-center justify-center p-6 text-center border border-[#588157]/10" data-id="element-284">
                  <UsersIcon size={40} className="mb-4 text-[#588157]" data-id="element-285" />
                  <span className="text-2xl font-serif font-bold text-[#588157]" data-id="element-286">
                    SYMBIS
                  </span>
                  <span className="text-xs uppercase tracking-widest text-[#1A374D]/70" data-id="element-287">
                    Marriage Coaching
                  </span>
                </div>
              </div>
              <div className="space-y-4" data-id="element-288">
                <div className="aspect-square bg-white rounded-2xl flex flex-col items-center justify-center p-6 text-center border border-[#588157]/10" data-id="element-289">
                  <ShieldCheckIcon size={40} className="mb-4 text-[#588157]" data-id="element-290" />
                  <span className="text-2xl font-serif font-bold text-[#588157]" data-id="element-291">
                    HIPAA
                  </span>
                  <span className="text-xs uppercase tracking-widest text-[#1A374D]/70" data-id="element-292">
                    Secure & Private
                  </span>
                </div>
                <div className="aspect-square bg-[#D4A373] rounded-2xl flex flex-col items-center justify-center text-white p-6 text-center" data-id="element-293">
                  <HeartPulseIcon size={40} className="mb-4" data-id="element-294" />
                  <span className="text-2xl font-serif font-bold" data-id="element-295">
                    Holistic
                  </span>
                  <span className="text-xs uppercase tracking-widest opacity-70" data-id="element-296">
                    Mind & Body
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Target Clients Section */}
      <section className="py-20 bg-white" data-id="element-297">
        <div className="container mx-auto px-4" data-id="element-298">
          <div className="max-w-4xl mx-auto text-center" data-id="element-299">
            <h2 className="text-3xl font-serif text-[#588157] mb-12" data-id="element-300">
              Who We Serve
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8" data-id="element-301">
              {[{
              label: 'Individuals & Families',
              icon: UsersIcon
            }, {
              label: 'Veterans & Survivors',
              icon: ShieldCheckIcon
            }, {
              label: 'Couples & Partners',
              icon: HeartPulseIcon
            }, {
              label: 'Employers & Orgs',
              icon: AwardIcon
            }].map(item => <div key={item.label} className="flex flex-col items-center gap-4" data-id="element-302">
                  <div className="w-16 h-16 bg-[#F8F9FA] rounded-2xl flex items-center justify-center text-[#588157]" data-id="element-303">
                    <item.icon size={32} data-id="element-304" />
                  </div>
                  <span className="font-bold text-[#1A374D] text-sm" data-id="element-305">
                    {item.label}
                  </span>
                </div>)}
            </div>
            <p className="mt-12 text-[#1A374D]/70 text-sm" data-id="element-306">
              Also serving youth, adolescents, healthcare patients, and
              government agencies.
            </p>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-24 bg-[#F8F9FA]" data-id="element-307">
        <div className="container mx-auto px-4" data-id="element-308">
          <div className="text-center mb-16" data-id="element-309">
            <h2 className="text-4xl font-serif text-[#588157] mb-4" data-id="element-310">
              Why Choose Global Restorative?
            </h2>
            <p className="text-[#1A374D]/70 max-w-2xl mx-auto" data-id="element-311">
              Our competitive advantage lies in our integrated approach and
              specialized expertise.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-312">
            {[{
            title: 'Integrated Care',
            desc: 'Seamlessly combining medical and mental wellness services for whole-person health.',
            icon: HeartPulseIcon
          }, {
            title: 'Expert Leadership',
            desc: 'Led by an experienced Nurse Practitioner and a Board-Certified Master Mental Health Coach.',
            icon: AwardIcon
          }, {
            title: 'Trauma-Informed',
            desc: 'Compassionate, client-centered care that respects your journey and confidentiality.',
            icon: ShieldCheckIcon
          }, {
            title: 'Restorative Approach',
            desc: 'Focusing on prevention and empowerment to help you rebuild a purposeful life.',
            icon: GraduationCapIcon
          }].map((feature, idx) => <motion.div key={feature.title} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: idx * 0.1
          }} className="bg-white p-8 rounded-2xl shadow-sm border border-[#588157]/5 text-center" data-id="element-313">
                <div className="w-14 h-14 bg-[#588157]/10 rounded-full flex items-center justify-center text-[#588157] mx-auto mb-6" data-id="element-314">
                  <feature.icon size={28} data-id="element-315" />
                </div>
                <h3 className="text-xl font-bold text-[#588157] mb-3" data-id="element-316">
                  {feature.title}
                </h3>
                <p className="text-sm text-[#1A374D]/70 leading-relaxed" data-id="element-317">
                  {feature.desc}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Trust Signals / Footer CTA */}
      <section className="py-20 bg-[#588157] text-white" data-id="element-318">
        <div className="container mx-auto px-4 text-center" data-id="element-319">
          <h2 className="text-4xl font-serif mb-8" data-id="element-320">
            Ready to start your journey?
          </h2>
          <p className="text-xl text-white/80 mb-12 max-w-2xl mx-auto" data-id="element-321">
            Whether you need clinical care or emotional support, we are here to
            help you restore, renew, and rebuild.
          </p>
          <div className="flex flex-wrap justify-center gap-6" data-id="element-322">
            <button onClick={() => window.location.hash = '#contact-us'} className="px-10 py-4 bg-[#D4A373] text-white font-bold rounded-full hover:bg-[#c29263] transition-all shadow-xl text-lg" data-id="element-323">
              Book Your Consultation
            </button>
            <button className="px-10 py-4 bg-transparent border-2 border-white text-white font-bold rounded-full hover:bg-white/10 transition-all text-lg" data-id="element-324">
              New Patient Portal
            </button>
          </div>

          <div className="mt-20 pt-10 border-t border-white/10 flex flex-wrap justify-center gap-12 opacity-60 grayscale hover:grayscale-0 transition-all" data-id="element-325">
            <div className="flex items-center gap-2" data-id="element-326">
              <ShieldCheckIcon size={24} data-id="element-327" />
              <span className="font-bold tracking-widest text-sm" data-id="element-328">
                HIPAA COMPLIANT
              </span>
            </div>
            <div className="flex items-center gap-2" data-id="element-329">
              <AwardIcon size={24} data-id="element-330" />
              <span className="font-bold tracking-widest text-sm" data-id="element-331">
                BOARD CERTIFIED
              </span>
            </div>
            <div className="flex items-center gap-2" data-id="element-332">
              <UsersIcon size={24} data-id="element-333" />
              <span className="font-bold tracking-widest text-sm" data-id="element-334">
                SYMBIS CERTIFIED
              </span>
            </div>
          </div>
        </div>
      </section>
    </main>;
}